import { ReactNode, useCallback, useMemo, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { Badge } from '@givebutter/primitives/badge';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import * as ButtonGroup from '@givebutter/primitives/button-group';
import { Label } from '@givebutter/primitives/label';
import { faArrowProgress, faEdit, faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FetchWorkflowsParams, fetchWorkflows } from 'api/workflows';
import * as DeleteWorkflowAlertDialog from 'assets/js/pages/engage/workflows/components/DeleteWorkflowAlertDialog';
import { UnpublishedChangesBadge } from 'assets/js/pages/engage/workflows/components/UnpublishedChangesBadge';
import { useUpdateWorkflowStatus } from 'assets/js/pages/engage/workflows/hooks/useUpdateWorkflowStatus';
import { getNextStatusData } from 'assets/js/pages/engage/workflows/utils/helpers';
import { useReactFlowStore } from 'assets/js/pages/engage/workflows/utils/store';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Input from 'components/input';
import * as Table from 'components/table';
import { useSyncPaginatedData } from 'components/table/useSyncPaginatedData';
import { IntercomProductTourTargets } from 'constants/intercom';
import { useLocalStorage } from 'contexts/useLocalStorage';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import useDebounce from 'customHooks/useDebounce';
import { useIsPending } from 'customHooks/useIsPending';
import { isoDateFromServer } from 'helpers/dates';
import WorkflowsEmptyStateBackground from 'images/workflows-empty-state-bg.svg?react';
import WorkflowsEmptyStateCtaImage from 'images/workflows-empty-state-cta.jpg';
import { DateTime } from 'luxon';
import { workflowQueryKeys } from 'queries/workflow-query-keys';
import { twJoin } from 'tailwind-merge';
import { Workflow, WorkflowStatus } from 'types/domain';

const columnHelper = createColumnHelper<Workflow>();

const WorkflowsIndex = () => {
    const { accountId } = useCurrentAccount();
    const match = useRouteMatch();
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search);
    const {
        workflowsIndexTab,
        setWorkflowsIndexTab,
        hasDismissedEmptyStateCta,
        setHasDismissedEmptyStateCta,
    } = useLocalStorage(state => state);
    const { pagination, setPagination, sorting, setSorting } = Table.useReactTableState({
        sortingState: [{ id: 'created_at', desc: true }],
    });

    const [isPending, params] = useIsPending<FetchWorkflowsParams>({
        page: pagination.pageIndex + 1,
        per_page: pagination.pageSize,
        query: debouncedSearch,
        statuses: [workflowsIndexTab],
        ...(sorting.length > 0
            ? { [`sortBy${sorting[0].desc ? 'Desc' : ''}`]: sorting[0].id }
            : {}),
    });

    const { data: workflows } = useSuspenseQuery({
        queryKey: workflowQueryKeys.workflowsWithFilters(accountId, params),
        queryFn: () => fetchWorkflows(accountId, params),
    });
    const { totals } = workflows;
    const showEmptyState = !totals || totals.total === 0;

    const { mutate: updateWorkflowStatus } = useUpdateWorkflowStatus();

    const resetNodeStore = useReactFlowStore(state => state.reset);

    const columns = useMemo(
        () => [
            columnHelper.accessor('name', {
                header: 'Name',
                enableSorting: true,
                meta: {
                    sortType: 'alpha',
                },
                cell: info => {
                    const { icon, name } = info.row.original;
                    return (
                        <span className="flex items-center gap-2 font-medium text-gray-900">
                            <span>{icon}</span>
                            <span>{name}</span>
                        </span>
                    );
                },
            }),
            columnHelper.accessor('last_triggered_at', {
                header: 'Last triggered',
                enableSorting: true,
                meta: {
                    sortType: 'date',
                },
                cell: info => {
                    const value = info.getValue();
                    return value
                        ? isoDateFromServer(value).toLocaleString(DateTime.DATE_FULL)
                        : 'Never';
                },
            }),
            columnHelper.accessor('created_at', {
                header: 'Date created',
                enableSorting: true,
                meta: {
                    sortType: 'date',
                },
                cell: info => isoDateFromServer(info.getValue()).toLocaleString(DateTime.DATE_FULL),
            }),
            columnHelper.display({
                id: 'actions',
                meta: {
                    ignoreLink: true,
                },
                cell: info => {
                    const { id, status, has_draft_version } = info.row.original;
                    const { buttonText, nextStatus, icon } = getNextStatusData(status);
                    const onUpdateStatusSuccess = () => setWorkflowsIndexTab(nextStatus);
                    const handleUpdateStatus = () =>
                        updateWorkflowStatus(
                            { workflowId: id, status: nextStatus },
                            { onSuccess: onUpdateStatusSuccess },
                        );
                    return (
                        <div className="flex w-full items-center justify-end gap-2">
                            {has_draft_version ? <UnpublishedChangesBadge /> : null}

                            <DropdownMenu.Root key={id}>
                                <Table.RowDropdownTrigger />
                                <DropdownMenu.Content sideOffset={5} align="end">
                                    <DropdownMenu.LinkItem to={`${match.url}/${id}`}>
                                        <div className="flex gap-2">
                                            <FontAwesomeIcon icon={faArrowProgress} />
                                            View Details
                                        </div>
                                    </DropdownMenu.LinkItem>
                                    {workflowsIndexTab === 'active' && (
                                        <DropdownMenu.Item
                                            onClick={handleUpdateStatus}
                                            className="flex gap-2"
                                        >
                                            <FontAwesomeIcon icon={icon} />
                                            {buttonText}
                                        </DropdownMenu.Item>
                                    )}
                                    <DropdownMenu.LinkItem
                                        to={`${match.url}/${id}/build`}
                                        onClick={() => resetNodeStore()}
                                    >
                                        <div className="flex gap-2">
                                            <FontAwesomeIcon icon={faEdit} />
                                            Edit
                                        </div>
                                    </DropdownMenu.LinkItem>

                                    <DeleteWorkflowAlertDialog.Root workflowId={id}>
                                        <DeleteWorkflowAlertDialog.Trigger asChild>
                                            <DropdownMenu.Item
                                                onSelect={e => e.preventDefault()}
                                                className="flex w-full gap-2 focus-visible:before:border-0"
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                                Delete
                                            </DropdownMenu.Item>
                                        </DeleteWorkflowAlertDialog.Trigger>
                                    </DeleteWorkflowAlertDialog.Root>
                                </DropdownMenu.Content>
                            </DropdownMenu.Root>
                        </div>
                    );
                },
            }),
        ],
        [match, workflowsIndexTab, updateWorkflowStatus, setWorkflowsIndexTab, resetNodeStore],
    );

    const table = useReactTable({
        data: workflows?.data ?? [],
        columns,
        pageCount: workflows?.meta.last_page ?? -1,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        state: {
            pagination,
            sorting,
        },
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        manualPagination: true,
        manualSorting: true,
        enableHiding: false,
        enableColumnPinning: false,
        meta: {
            to: cell => `/accounts/${accountId}/workflows/${cell.row.original.id}`,
        },
    });

    useSyncPaginatedData(table, workflows);

    const EmptyDescriptionText = useCallback(() => {
        switch (workflowsIndexTab) {
            case 'paused':
                return "You don't have any paused workflows right now. You can pause active workflows at any time.";
            case 'draft':
                return "You don't have any draft workflows right now. You can create new workflows and continue to edit them here.";
            case 'active':
            default:
                return 'Automate your tasks and accelerate the impact by getting started with your first workflow.';
        }
    }, [workflowsIndexTab]);

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-10">
                {showEmptyState && !hasDismissedEmptyStateCta ? (
                    <Box className="flex flex-col items-stretch overflow-hidden md:flex-row">
                        <div className="min-w-96 flex-shrink-0 basis-1/4">
                            <img
                                className="block min-h-full object-cover"
                                src={WorkflowsEmptyStateCtaImage}
                                alt="Givebutter Workflows"
                            />
                        </div>
                        <div className="relative flex flex-grow flex-col justify-center gap-4 p-6">
                            <WorkflowsEmptyStateBackground />
                            <div>
                                <p className="text-lg font-medium">
                                    Level up your donor game, hit auto-pilot on tasks.
                                </p>
                                <p className="text-sm">
                                    Save yourself the hassle of manually sending messages and doing
                                    tedious tasks. Set up an automated workflow to boost engagement
                                    with personalized interactions.
                                </p>
                            </div>
                            <div className="flex items-center gap-3">
                                <Button
                                    variant="outline"
                                    color="neutral"
                                    onClick={() => setHasDismissedEmptyStateCta(true)}
                                >
                                    Dismiss
                                </Button>
                                <NewWorkflowButton>Get started</NewWorkflowButton>
                            </div>
                        </div>
                    </Box>
                ) : null}
                <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <h1 className="text-2xl font-medium">Workflows</h1>
                        {totals ? (
                            <>
                                <div className="h-6 w-px bg-gray-300"></div>
                                <p className="text-base">{totals.total}</p>
                            </>
                        ) : null}
                    </div>
                    <NewWorkflowButton>
                        <FontAwesomeIcon icon={faPlus} />
                        New workflow
                    </NewWorkflowButton>
                </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-4">
                <ButtonGroup.Root
                    type="single"
                    value={workflowsIndexTab}
                    onValueChange={(value: WorkflowStatus) => setWorkflowsIndexTab(value)}
                >
                    <ButtonGroup.Item value="active" className="gap-2">
                        Active
                        {totals && totals.total_active > 0 && (
                            <Badge
                                size="sm"
                                className={twJoin(
                                    'group-radix-state-on:bg-blue-50 group-radix-state-on:text-blue-600 bg-gray-200',
                                )}
                            >
                                {totals.total_active}
                            </Badge>
                        )}
                    </ButtonGroup.Item>
                    <ButtonGroup.Item value="paused" className="gap-2">
                        Paused
                        {totals && totals.total_paused > 0 && (
                            <Badge
                                size="sm"
                                className={twJoin(
                                    'group-radix-state-on:bg-blue-50 group-radix-state-on:text-blue-600 bg-gray-200',
                                )}
                            >
                                {totals.total_paused}
                            </Badge>
                        )}
                    </ButtonGroup.Item>
                    <ButtonGroup.Item value="draft" className="gap-2">
                        Draft
                        {totals && totals.total_draft > 0 && (
                            <Badge
                                size="sm"
                                className={twJoin(
                                    'group-radix-state-on:bg-blue-50 group-radix-state-on:text-blue-600 bg-gray-200',
                                )}
                            >
                                {totals.total_draft}
                            </Badge>
                        )}
                    </ButtonGroup.Item>
                </ButtonGroup.Root>
                <Label className="sr-only" htmlFor="workflows_search">
                    Search
                </Label>
                <Input.Root size={'sm'}>
                    <Input.SearchIcon />
                    <Input.Form
                        id="workflows_search"
                        placeholder={'Search'}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </Input.Root>
            </div>
            <Table.Root variant="bordered" table={table} searchTerm={search} isLoading={isPending}>
                <div className="flex flex-col gap-y-4">
                    <Box className="overflow-x-auto">
                        <Table.Content>
                            <Table.Header />
                            <Table.Body />
                        </Table.Content>
                        <Table.Empty variant="primary" className="max-w-full">
                            <Table.EmptyIcon>
                                <FontAwesomeIcon icon={faArrowProgress} />
                            </Table.EmptyIcon>
                            <Table.EmptyTitle>
                                {!totals
                                    ? "You haven't created any workflows yet"
                                    : 'No workflows found'}
                            </Table.EmptyTitle>
                            <Table.EmptyDescription className="max-w-80">
                                <EmptyDescriptionText />
                            </Table.EmptyDescription>
                        </Table.Empty>
                        <Table.Pagination className={'border-t border-t-gray-100 p-2'}>
                            <Table.PaginationPageNavigation />
                            <Table.PaginationPerPageDropdown />
                        </Table.Pagination>
                    </Box>
                </div>
            </Table.Root>
        </div>
    );
};

const NewWorkflowButton = ({ children }: { children?: ReactNode }) => {
    const { accountId } = useCurrentAccount();
    const history = useHistory();
    return (
        <Button
            variant="solid"
            data-intercom-target={IntercomProductTourTargets.WorkflowsNewWorkflowButton}
            onClick={() =>
                history.push({
                    pathname: `/accounts/${accountId}/workflows/new`,
                })
            }
        >
            {children}
        </Button>
    );
};

export { WorkflowsIndex };
